import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Button, Empty, Grid, Spin } from "@arco-design/web-react";
import { IconDown } from "@arco-design/web-react/icon";
import "./index.scss";
import { isBoolean, isObject } from "@/utils/is";
import { InfiniteRenderType, InfiniteScrollProps } from "./interface";

const { Row } = Grid;

export default function InfiniteScroll<T>(
  props: InfiniteScrollProps<T>
  // ref: React.Ref<HTMLDivElement>
) {
  const { loading, data = [], scrollToBottomTrigger, renderItem } = props;

  const pageProps = useMemo(() => {
    if (props.type === InfiniteRenderType.PAGINATED_BY_PAGE) {
      return {
        ...props?.pagination,
        currentPage: props?.pagination?.currentPage || 1,
      };
    }
    return undefined;
  }, [props]);

  const hasMoreRef = useRef<HTMLDivElement>(null);

  const [hasMore, $hasMore] = useState(true);
  const [isEnableMore, $isEnableMore] = useState<boolean>(false);

  useEffect(() => {
    if (pageProps?.currentPage === 1) {
      $isEnableMore(false);
      if (pageProps?.totalPage !== 1) {
        $hasMore(true);
      }
    }
  }, [pageProps]);

  const handleChange = useCallback(() => {
    if (pageProps?.totalPage) {
      if (pageProps?.currentPage < pageProps?.totalPage) {
        if (pageProps?.loadMorePage) {
          if (pageProps?.currentPage === pageProps?.loadMorePage) {
            $isEnableMore(true);
          } else {
            scrollToBottomTrigger?.(pageProps?.currentPage + 1);
          }
        } else {
          scrollToBottomTrigger?.(pageProps?.currentPage + 1);
        }
      } else {
        $hasMore(false);
      }
    }
  }, [pageProps, scrollToBottomTrigger]);

  const isLoading = useMemo(() => {
    if (isBoolean(loading)) {
      return loading;
    }
    return loading.loading;
  }, [loading]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target?.isIntersecting && hasMore && !isLoading) {
          handleChange();
        }
      },
      {
        root: null,
        rootMargin: "20px",
        threshold: 0.1,
      }
    );

    if (hasMoreRef.current) {
      observer.observe(hasMoreRef.current);
    }

    return () => observer.disconnect();
  }, [handleChange, hasMore, isLoading]);

  const loadMore = useCallback(() => {
    if (pageProps) {
      $isEnableMore(false);
      scrollToBottomTrigger?.(pageProps?.currentPage + 1);
    }
  }, [scrollToBottomTrigger, pageProps]);

  const enableMore = useMemo(() => {
    if (isLoading) {
      return false;
    }
    return isEnableMore;
  }, [isEnableMore, isLoading]);

  const el = useMemo(() => {
    if (data?.length) {
      return (
        <Row gutter={[16, 16]} className="yf-infinite-scroll-list">
          {data.map((item, index) => renderItem(item, index))}
        </Row>
      );
    }
    return <Empty description="no products" />;
  }, [data, renderItem]);

  const isLoadingBottom = useMemo(() => {
    if (isObject(loading)) {
      if (loading.position === "bottom") {
        return true;
      }
      return false;
    }
    return false;
  }, [loading]);

  return (
    <div className="yf-infinite-scroll">
      <Spin
        className="yf-infinite-scroll-spin"
        tip="loading..."
        {...(!isLoadingBottom && {
          loading: isLoading,
          ...(isObject(loading) && loading),
        })}
      >
        {el}
      </Spin>
      {isLoadingBottom && isLoading && (
        <Spin
          className={"mt-4"}
          tip={"loading..."}
          {...(isObject(loading) && loading)}
        />
      )}
      {hasMore && (
        <div ref={hasMoreRef} className="yf-infinite-scroll-has-more" />
      )}
      {enableMore && (
        <div className="yf-infinite-scroll-footer-actions">
          <Button
            className={"yf-explore-more-btn"}
            loading={isLoading}
            onClick={loadMore}
          >
            Please click here to explore more products... <IconDown />
          </Button>
        </div>
      )}
    </div>
  );
}
